import { useEffect, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const pdfjsLib = require('pdfjs-dist/legacy/build/pdf');

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const thumbnailCache = new Map<string, string>();
const TIMEOUT_MS = 10000;

type UsePdfThumbnailResult = {
  thumbnailUrl: string | null;
  isLoading: boolean;
  error: string | null;
};

export const usePdfThumbnail = (pdfUrl: string, scale = 1): UsePdfThumbnailResult => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pdfUrl) {
      setIsLoading(false);
      setError('URL no proporcionada');
      return;
    }

    const cached = thumbnailCache.get(pdfUrl);
    if (cached) {
      setThumbnailUrl(cached);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    let timeoutId: NodeJS.Timeout;

    const generateThumbnail = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Timeout para evitar spinner infinito
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Timeout')), TIMEOUT_MS);
        });

        const loadingTask = pdfjsLib.getDocument({ url: pdfUrl });
        const pdf = await Promise.race([loadingTask.promise, timeoutPromise]);

        if (cancelled) return;

        const page = await pdf.getPage(1);
        if (cancelled) return;

        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });

        if (!context) throw new Error('No se pudo crear canvas');

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;
        if (cancelled) return;

        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        thumbnailCache.set(pdfUrl, dataUrl);

        setThumbnailUrl(dataUrl);
        setIsLoading(false);
      } catch {
        if (cancelled) return;
        setError('Error al cargar PDF');
        setIsLoading(false);
      }
    };

    generateThumbnail();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [pdfUrl, scale]);

  return { thumbnailUrl, isLoading, error };
};

// Funcion para limpiar cache si es necesario
export const clearPdfThumbnailCache = () => {
  thumbnailCache.clear();
};
